import { toast } from "$global-providers/toast-provider";
import {
  Button,
  CircularProgress,
  styled,
  Typography,
} from "@material-ui/core";
import type { EpocusRole } from "elephant-web/gql";
import { phone } from "phone";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { EPOCUS_TEXT_SECONDARY, rem } from "../../../../../_lib/epocus-palette";
import { SettingsCard } from "../../../../../settings/_lib/settings-card";
import {
  ProgramUserForm,
  type ProgramUserFormValues,
} from "../../../../../settings/users/_lib/program-user-form";
import { useEpocusAdminAddProgramUserMutation } from "../../../../_lib/__generated__/epocus-admin-operations.gql";
import { AdminPageHeader } from "../../../../_lib/admin-page-header";
import {
  AdminLoadingRow,
  AdminPageRoot,
} from "../../../../_lib/admin-page-root";
import { useAdminProgram } from "../../../../_lib/use-admin-program";
import { linkToEpocusAdmin, linkToEpocusAdminProgram } from "../../../../links";

const BACK_BUTTON_STYLE = {
  textTransform: "none",
  padding: "10px 12px",
  marginLeft: -12,
  minHeight: 44,
  alignSelf: "flex-start",
} as const;

const MutedBody = styled(Typography)({
  fontSize: rem(13),
  color: EPOCUS_TEXT_SECONDARY,
});

export const EpocusAdminAddProgramUserPage: FC = () => {
  const { programId } = useParams();
  const { program, loading, error, notFound } = useAdminProgram(programId);
  const navigate = useNavigate();

  const [addUser, { loading: submitting }] =
    useEpocusAdminAddProgramUserMutation({
      onCompleted: () => {
        toast.success(
          "User added — they'll get an email to set their password",
        );
        navigate(linkToEpocusAdminProgram(programId ?? ""));
      },
      onError: (e) => toast.error(`Couldn't add user: ${e.message}`),
    });

  if (!programId) return <Navigate to={linkToEpocusAdmin()} replace />;

  const handleSubmit = async (values: ProgramUserFormValues) => {
    await addUser({
      variables: {
        input: {
          programId,
          role: values.role as EpocusRole,
          email: values.email,
          firstName: values.firstName,
          lastName: values.lastName,
          telephone: values.telephone
            ? phone(values.telephone).phoneNumber || values.telephone
            : undefined,
        },
      },
    });
  };

  const backButton = (
    <Button
      component={Link}
      to={linkToEpocusAdminProgram(programId)}
      style={BACK_BUTTON_STYLE}
    >
      ← Back to program
    </Button>
  );

  if (loading) {
    return (
      <AdminPageRoot>
        {backButton}
        <AdminLoadingRow>
          <CircularProgress aria-label="Loading program" />
        </AdminLoadingRow>
      </AdminPageRoot>
    );
  }

  if (error || notFound) {
    return (
      <AdminPageRoot>
        {backButton}
        <SettingsCard>
          <MutedBody role="alert">
            {error
              ? "Couldn't load this program."
              : "That program doesn't exist."}
          </MutedBody>
        </SettingsCard>
      </AdminPageRoot>
    );
  }

  return (
    <AdminPageRoot>
      {backButton}

      <AdminPageHeader
        title="Add user"
        subtitle={`They'll be added to ${program?.name ?? "this program"}`}
      />

      <SettingsCard>
        <ProgramUserForm
          submitLabel="Add user"
          submitting={submitting}
          emailEditable
          onSubmit={handleSubmit}
        />
      </SettingsCard>
    </AdminPageRoot>
  );
};
